import { useEffect, useState, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import {
  Box, Grid, Typography, Paper, Avatar, Button, Chip, Divider,
  Radio, RadioGroup, FormControlLabel, Checkbox, Select, MenuItem, FormControl,
  CircularProgress, IconButton, Collapse, Slider
} from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import SortOutlinedIcon from '@mui/icons-material/SortOutlined';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { searchAll } from '../../actions/profile';
import { getCategories, getSubcategories } from '../../actions/categories';
import { urlImg, isEmpty } from '../../components/common/helpers';
import SearchMap from '../../components/search/SearchMap';

const resolveUserAvatar = (avatar, userId, version) => {
  if (isEmpty(avatar)) return '';
  const a = avatar.toString();
  if (a.startsWith('http')) return a;
  if (a.startsWith('//')) return a.replace(/^\/\//, 'https://');
  const v = version ? `v${version}/` : '';
  return `${urlImg}/w_150,h_150,c_fill/${v}users/${userId}/${a}`;
};

const RatingChip = ({ rating }) => (
  rating > 0 ? (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
      <StarIcon sx={{ fontSize: 16, color: '#0E2465' }} />
      <Typography variant="body2" fontWeight={700} color="#0E2465">{rating.toFixed(1)}</Typography>
    </Box>
  ) : (
    <Typography variant="caption" fontWeight={700} color="#94A3B8">Nuevo</Typography>
  )
);

const PersonaCard = ({ persona, onVer }) => {
  const avatar = resolveUserAvatar(persona.avatar, persona.userId, persona.versionAvatar);
  return (
    <Paper elevation={0} sx={{ p: 2, mb: 2, borderRadius: '16px', border: '1px solid #F1F5F9', display: 'flex', gap: 2, alignItems: 'center' }}>
      <Box sx={{ position: 'relative', flexShrink: 0 }}>
        <Avatar src={avatar} sx={{ width: 90, height: 90, borderRadius: '12px' }} variant="rounded">
          {persona.name?.charAt(0)}
        </Avatar>
        <Chip icon={<VerifiedUserIcon sx={{ fontSize: 12, color: 'white !important' }} />} label="Verificado"
          sx={{ position: 'absolute', top: 6, left: 6, height: 18, bgcolor: 'rgba(14,36,101,0.75)', color: 'white', fontSize: '0.6rem', '& .MuiChip-label': { px: 0.5 } }} />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          <Typography variant="subtitle1" fontWeight={700} color="#0E2465" noWrap>
            {persona.name} {persona.apellido}
          </Typography>
          <RatingChip rating={persona.rating} />
        </Box>
        <Typography variant="body2" color="#737791" sx={{ mb: 1 }}>{persona.rol}</Typography>
        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <LocalShippingOutlinedIcon sx={{ fontSize: 16, color: '#0E2465' }} />
            <Typography variant="caption" color="#475569">{persona.serviceCount} servicios</Typography>
          </Box>
          <Typography variant="caption" color="#94A3B8">+{persona.ratingTotal} recomendaciones</Typography>
        </Box>
      </Box>
      <Button variant="contained" onClick={() => onVer(persona)} disableElevation
        sx={{ bgcolor: '#0E2465', textTransform: 'none', borderRadius: '8px', px: 3, flexShrink: 0, '&:hover': { bgcolor: '#0a1a4a' } }}>
        Ver perfil
      </Button>
    </Paper>
  );
};

const ServicioCard = ({ servicio, onVer }) => {
  const img = servicio.img ? `${urlImg}/w_400,h_250,c_fill/servicios/${servicio._id}/${servicio.img}` : '/search.png';
  const avatar = resolveUserAvatar(servicio.authorAvatar, servicio.userId, servicio.authorAvatarVersion);
  return (
    <Paper elevation={0} sx={{ p: 2, mb: 2, borderRadius: '16px', border: '1px solid #F1F5F9', display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
      <Box sx={{ position: 'relative', width: { xs: '100%', sm: 180 }, height: 130, borderRadius: '12px', overflow: 'hidden', flexShrink: 0 }}>
        <Box component="img" src={img} alt={servicio.nombre} onClick={() => onVer(servicio)} sx={{ width: '100%', height: '100%', objectFit: 'cover', cursor: 'pointer' }} />
        <Chip label={servicio.categoriaNombre} size="small"
          sx={{ position: 'absolute', top: 8, left: 8, height: 20, bgcolor: 'rgba(14,36,101,0.75)', color: 'white', fontSize: '0.65rem' }} />
        <Box sx={{ position: 'absolute', bottom: 6, left: 6, display: 'flex', alignItems: 'center', gap: 0.5, bgcolor: 'rgba(255,255,255,0.9)', borderRadius: '20px', px: 0.8, py: 0.2 }}>
          <Avatar src={avatar} sx={{ width: 18, height: 18 }} />
          <Typography variant="caption" fontWeight={700} color="#0E2465" sx={{ fontSize: '0.6rem' }} noWrap>{servicio.author}</Typography>
        </Box>
      </Box>
      <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          <Typography variant="subtitle1" fontWeight={700} color="#0E2465" noWrap>{servicio.nombre}</Typography>
          <RatingChip rating={servicio.rating} />
        </Box>
        <Typography variant="caption" color="#64748B" sx={{ mb: 1 }}>{servicio.author}</Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 1 }}>
          {servicio.ubicacion && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
              <LocationOnIcon sx={{ fontSize: 15, color: '#0E2465' }} />
              <Typography variant="caption" color="#475569" noWrap>{servicio.ubicacion}</Typography>
            </Box>
          )}
          {servicio.duracionEstimada && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
              <AccessTimeIcon sx={{ fontSize: 15, color: '#0E2465' }} />
              <Typography variant="caption" color="#475569">{servicio.duracionEstimada} {servicio.unidadDuracion}</Typography>
            </Box>
          )}
        </Box>
        <Box sx={{ mt: 'auto', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
          <Box>
            <Typography variant="h6" fontWeight={800} color="#0E2465" sx={{ lineHeight: 1 }}>
              ${Number(servicio.costoServicio).toFixed(2)}
            </Typography>
            <Typography variant="caption" color="#94A3B8">por {servicio.tipoCosto?.toLowerCase()}</Typography>
          </Box>
          <Button variant="contained" onClick={() => onVer(servicio)} disableElevation
            sx={{ bgcolor: '#0E2465', textTransform: 'none', borderRadius: '8px', '&:hover': { bgcolor: '#0a1a4a' } }}>
            Ver detalle
          </Button>
        </Box>
      </Box>
    </Paper>
  );
};

const PaginaCard = ({ pagina, onVer }) => {
  const img = pagina.img ? `${urlImg}/w_400,h_250,c_fill/paginas/${pagina._id}/${pagina.img}` : '/search.png';
  const avatar = resolveUserAvatar(pagina.authorAvatar, pagina.userId, pagina.authorAvatarVersion);
  return (
    <Paper elevation={0} sx={{ p: 2, mb: 2, borderRadius: '16px', border: '1px solid #F1F5F9', display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
      <Box sx={{ position: 'relative', width: { xs: '100%', sm: 180 }, height: 130, borderRadius: '12px', overflow: 'hidden', flexShrink: 0 }}>
        <Box component="img" src={img} alt={pagina.nombre} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        <Chip label={pagina.categoriaNombre} size="small"
          sx={{ position: 'absolute', top: 8, left: 8, height: 20, bgcolor: 'rgba(14,36,101,0.75)', color: 'white', fontSize: '0.65rem' }} />
        <Box sx={{ position: 'absolute', bottom: 6, left: 6, display: 'flex', alignItems: 'center', gap: 0.5, bgcolor: 'rgba(255,255,255,0.9)', borderRadius: '20px', px: 0.8, py: 0.2 }}>
          <Avatar src={avatar} sx={{ width: 18, height: 18 }} />
          <Typography variant="caption" fontWeight={700} color="#0E2465" sx={{ fontSize: '0.6rem' }} noWrap>{pagina.author}</Typography>
        </Box>
      </Box>
      <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          <Typography variant="subtitle1" fontWeight={700} color="#0E2465" noWrap>{pagina.nombre}</Typography>
          <RatingChip rating={pagina.rating} />
        </Box>
        <Typography variant="body2" color="#64748B" sx={{
          display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', mb: 1
        }}>
          {pagina.descripcion}
        </Typography>
        <Box sx={{ mt: 'auto', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
          <Box>
            <Typography variant="h6" fontWeight={800} color="#0E2465" sx={{ lineHeight: 1 }}>{pagina.serviciosDisponibles}</Typography>
            <Typography variant="caption" color="#94A3B8">Servicios disponibles</Typography>
          </Box>
          <Button variant="contained" onClick={() => onVer(pagina)} disableElevation
            sx={{ bgcolor: '#0E2465', textTransform: 'none', borderRadius: '8px', '&:hover': { bgcolor: '#0a1a4a' } }}>
            Ver página
          </Button>
        </Box>
      </Box>
    </Paper>
  );
};

const Section = ({ title, subtitle, count, itemHeight = 170, children }) => {
  // Mostrar ~5 tarjetas; si hay más, activar scroll (la 6ª asoma para indicar que hay más)
  const scroll = count > 5;
  return (
    <Box sx={{ mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 1.5 }}>
        <Box>
          <Typography variant="h6" fontWeight={700} color="#0E2465">{title}</Typography>
          <Typography variant="body2" color="#737791">{subtitle}</Typography>
        </Box>
        {count > 0 && <Typography variant="body2" fontWeight={700} color="#7495f8">{count} resultado{count !== 1 ? 's' : ''}</Typography>}
      </Box>
      <Box sx={scroll ? { maxHeight: itemHeight * 5.4, overflowY: 'auto', pr: 1 } : {}}>
        {children}
      </Box>
    </Box>
  );
};

const SearchResults = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const categories = useSelector(state => state.cat.categories);

  const q = searchParams.get('q') || '';
  const categoria = searchParams.get('categoria') || '';
  const subcategoria = searchParams.get('subcategoria') || '';

  const [data, setData] = useState({ personas: [], servicios: [], paginasIndependientes: [], paginasGrupos: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [subcategories, setSubcategories] = useState([]);

  // Filtros (cliente)
  const [orden, setOrden] = useState('rating');
  const [tipos, setTipos] = useState({ personas: true, servicios: true, independientes: true, grupos: true });
  const [precio, setPrecio] = useState([0, 10000]);
  const [advOpen, setAdvOpen] = useState(false);

  useEffect(() => {
    if (!categories || categories.length === 0) dispatch(getCategories());
  }, [dispatch]);

  useEffect(() => {
    if (categoria) getSubcategories(categoria).then(d => setSubcategories(d || []));
    else setSubcategories([]);
  }, [categoria]);

  useEffect(() => {
    setLoading(true);
    searchAll({ q, categoria, subcategoria }).then(d => {
      setData(d);
      setLoading(false);
    });
  }, [q, categoria, subcategoria]);

  const updateParam = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value); else next.delete(key);
    if (key === 'categoria') next.delete('subcategoria');
    setSearchParams(next);
  };

  // Aplicar filtros cliente (precio) y orden
  const procesar = (lista, tienePrecio) => {
    let res = [...lista];
    if (tienePrecio) res = res.filter(x => x.costoServicio >= precio[0] && x.costoServicio <= precio[1]);
    if (tienePrecio && orden === 'priceAsc') res.sort((a, b) => a.costoServicio - b.costoServicio);
    else if (tienePrecio && orden === 'priceDesc') res.sort((a, b) => b.costoServicio - a.costoServicio);
    else res.sort((a, b) => (b.rating || 0) - (a.rating || 0));
    return res;
  };

  const personas = useMemo(() => procesar(data.personas, false), [data, orden]);
  const servicios = useMemo(() => procesar(data.servicios, true), [data, orden, precio]);
  const independientes = useMemo(() => procesar(data.paginasIndependientes, false), [data, orden]);
  const grupos = useMemo(() => procesar(data.paginasGrupos, false), [data, orden]);

  const totalVisible =
    (tipos.personas ? personas.length : 0) +
    (tipos.servicios ? servicios.length : 0) +
    (tipos.independientes ? independientes.length : 0) +
    (tipos.grupos ? grupos.length : 0);

  return (
    <Box className="main-page" sx={{ mb: 8 }}>
      {/* Header */}
      <Box sx={{ bgcolor: '#7495f8', color: 'white', borderRadius: 2, px: 4, py: 5, mb: 3 }}>
        <Typography variant="h5" fontWeight={700}>Resultados de búsqueda</Typography>
        <Typography mt={2} variant="body1" color="primary">
          <small>
            {loading ? 'Buscando…' : `Encontramos ${totalVisible} resultado${totalVisible !== 1 ? 's' : ''}`}
            {q ? ` para "${q}"` : ''}
          </small>
        </Typography>
      </Box>

      <Grid container spacing={4}>
        {/* Sidebar de filtros */}
        <Grid size={{ xs: 12, md: 3.5 }}>
          <Paper elevation={0} sx={{ p: 3, borderRadius: '16px', border: '1px solid #F1F5F9', position: { md: 'sticky' }, top: { md: 100 } }}>
            <Typography variant="h6" fontWeight={700} color="#0E2465" sx={{ mb: 2 }}>Filtrar búsqueda</Typography>

            {/* Ordenar */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <SortOutlinedIcon sx={{ fontSize: 18, color: '#737791' }} />
              <Typography variant="caption" fontWeight={700} color="#737791">ORDENAR POR</Typography>
            </Box>
            <RadioGroup value={orden} onChange={e => setOrden(e.target.value)}>
              <FormControlLabel value="rating" control={<Radio size="small" />} label={<Typography variant="body2">Mejores calificaciones</Typography>} />
              <FormControlLabel value="priceAsc" control={<Radio size="small" />} label={<Typography variant="body2">Menor precio</Typography>} />
              <FormControlLabel value="priceDesc" control={<Radio size="small" />} label={<Typography variant="body2">Mayor precio</Typography>} />
            </RadioGroup>
            <Divider sx={{ my: 2 }} />

            {/* Categoría */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <CategoryOutlinedIcon sx={{ fontSize: 18, color: '#737791' }} />
              <Typography variant="caption" fontWeight={700} color="#737791">CATEGORÍA</Typography>
            </Box>
            <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
              <Select displayEmpty value={categoria} onChange={e => updateParam('categoria', e.target.value)}
                sx={{ borderRadius: '8px' }}>
                <MenuItem value=""><em>Selecciona categoría</em></MenuItem>
                {(categories || []).map(c => <MenuItem key={c._id} value={c._id}>{c.name}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl fullWidth size="small" disabled={!categoria}>
              <Select displayEmpty value={subcategoria} onChange={e => updateParam('subcategoria', e.target.value)}
                sx={{ borderRadius: '8px' }}>
                <MenuItem value=""><em>Selecciona subcategoría</em></MenuItem>
                {subcategories.map(s => <MenuItem key={s._id} value={s._id}>{s.name}</MenuItem>)}
              </Select>
            </FormControl>
            <Divider sx={{ my: 2 }} />

            {/* Tipo de búsqueda */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <SearchOutlinedIcon sx={{ fontSize: 18, color: '#737791' }} />
              <Typography variant="caption" fontWeight={700} color="#737791">TIPO DE BÚSQUEDA</Typography>
            </Box>
            {[
              ['personas', 'Personas'],
              ['servicios', 'Solo servicios'],
              ['independientes', 'Páginas de servicios independientes'],
              ['grupos', 'Páginas de grupos y negocios'],
            ].map(([key, label]) => (
              <FormControlLabel key={key}
                control={<Checkbox size="small" checked={tipos[key]} onChange={e => setTipos(t => ({ ...t, [key]: e.target.checked }))} />}
                label={<Typography variant="body2">{label}</Typography>}
                sx={{ display: 'flex' }}
              />
            ))}
            <Divider sx={{ my: 2 }} />

            {/* Filtros avanzados */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}
              onClick={() => setAdvOpen(o => !o)}>
              <Typography variant="subtitle2" fontWeight={700} color="#0E2465">Filtros avanzados</Typography>
              <IconButton size="small"><ExpandMoreIcon sx={{ transform: advOpen ? 'rotate(180deg)' : 'none', transition: '0.2s' }} /></IconButton>
            </Box>
            <Collapse in={advOpen}>
              <Box sx={{ px: 1, pt: 1 }}>
                <Typography variant="caption" color="#737791">Rango de precio (servicios)</Typography>
                <Slider value={precio} onChange={(e, v) => setPrecio(v)} min={0} max={10000} step={100}
                  valueLabelDisplay="auto" sx={{ color: '#0E2465', mt: 1 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="caption" color="#475569">${precio[0]}</Typography>
                  <Typography variant="caption" color="#475569">${precio[1]}</Typography>
                </Box>
              </Box>
            </Collapse>
          </Paper>
        </Grid>

        {/* Contenido */}
        <Grid size={{ xs: 12, md: 8.5 }}>
          {/* Mapa con ubicaciones de los servicios encontrados */}
          <SearchMap servicios={servicios} />

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress sx={{ color: '#7495f8' }} />
            </Box>
          ) : totalVisible === 0 ? (
            <Paper elevation={0} sx={{ p: 6, borderRadius: '16px', border: '1px solid #F1F5F9', textAlign: 'center' }}>
              <Typography variant="h6" fontWeight={700} color="#0E2465" sx={{ mb: 1 }}>Sin resultados</Typography>
              <Typography variant="body2" color="#737791">
                No encontramos coincidencias{q ? ` para "${q}"` : ''}. Intenta con otra palabra o ajusta los filtros.
              </Typography>
            </Paper>
          ) : (
            <>
              {tipos.personas && personas.length > 0 && (
                <Section title="Personas" subtitle="Proveedores que coinciden con tu búsqueda" count={personas.length} itemHeight={138}>
                  {personas.map(p => <PersonaCard key={p.userId} persona={p} onVer={(x) => navigate(`/perfil/${x.userId}`)} />)}
                </Section>
              )}
              {tipos.servicios && servicios.length > 0 && (
                <Section title="Solo servicios" subtitle="Servicios asociados a tu búsqueda" count={servicios.length} itemHeight={178}>
                  {servicios.map(s => <ServicioCard key={s._id} servicio={s} onVer={(x) => navigate(`/servicio/${x.userId}/${x._id}`)} />)}
                </Section>
              )}
              {tipos.independientes && independientes.length > 0 && (
                <Section title="Páginas de servicios independientes" subtitle="Profesionales con su propia página" count={independientes.length} itemHeight={178}>
                  {independientes.map(p => <PaginaCard key={p._id} pagina={p} onVer={(x) => navigate(`/pagina/${x._id}`)} />)}
                </Section>
              )}
              {tipos.grupos && grupos.length > 0 && (
                <Section title="Páginas de grupos y negocios" subtitle="Empresas y equipos especializados" count={grupos.length} itemHeight={178}>
                  {grupos.map(p => <PaginaCard key={p._id} pagina={p} onVer={(x) => navigate(`/pagina/${x._id}`)} />)}
                </Section>
              )}
            </>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default SearchResults;
